import { UseFormSetError, FieldValues, Path } from "react-hook-form";
import { AxiosError } from "axios";
import { ApiErrorResponse } from "../types/response";
import { failedToast } from "./toasts";

/**
 * Handles API errors by mapping them to React Hook Form fields or showing a toast.
 *
 * @param error The Axios error object
 * @param setError The setError function from useForm
 * @param defaultMessage A fallback message if no specific error is found
 */
export const handleApiErrors = <T extends FieldValues>(
  error: AxiosError<ApiErrorResponse>,
  setError: UseFormSetError<T>,
  defaultMessage: string = "Une erreur est survenue",
) => {
  const data = error.response?.data;

  if (!data) {
    failedToast(defaultMessage);
    return;
  }

  // Handle flat object error format: {"field": "error message"}
  // Also handles {"field": ["error message"]}
  let hasFieldErrors = false;

  Object.entries(data).forEach(([key, value]) => {
    // Skip 'message' and 'detail' as they are often general or special
    if (key === "message" || key === "detail") return;

    if (typeof value === "string") {
      setError(key as Path<T>, { type: "server", message: value });
      hasFieldErrors = true;
    } else if (
      Array.isArray(value) &&
      value.length > 0 &&
      typeof value[0] === "string"
    ) {
      setError(key as Path<T>, { type: "server", message: value[0] });
      hasFieldErrors = true;
    }
  });

  // Handle Strapi style 'detail' object if present
  if (data.detail && typeof data.detail === "object") {
    Object.entries(data.detail).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        setError(key as Path<T>, { type: "server", message: value[0] });
        hasFieldErrors = true;
      }
    });
  }

  // If no field errors were found, show the main message or detail as a toast
  if (!hasFieldErrors) {
    const mainMessage =
      data.message ||
      (typeof data.detail === "string" ? data.detail : null) ||
      defaultMessage;
    failedToast(mainMessage as string);
  }
};
